import styled from 'styled-components'
import { t } from 'ttag'
import { useEffect, useCallback, useMemo, useContext } from 'react'

import { features } from '@h2/components/media'
import { PG, SLB, NAVIANCEBLUE } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { AutoSuggest } from '@h2/components/inputs/autoSuggest'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Card } from '@h2/common/components/card'
import { US_STATES_LIST } from '@h2/common/constants'
import { Globals } from '@h2/globals'

import { props } from './props'

import { Explorer } from '../highlights/explorer'
import { Navigator } from '../highlights/navigator'
import { Controls } from '../controls'
import { Submit } from '../submit'
import { Back } from '../back'
import {
  getFutureGraduationItems,
  loadSchools,
  renderSchool
} from '../../../utils/schools'
import {
  getStateFromAbbreviation,
  parseStateValueFromCustomInput
} from '../../../utils/states'

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;

  > h2 {
    font-size: 24px;
    line-height: 28px;
  }

  > div:nth-child(n + 2) {
    margin: 15px 0 0 0;
  }

  > div:last-child {
    margin-top: auto;
  }

  @media (${features.minWidth.laptop}) {
    > h2 {
      font-size: 30px;
      line-height: 34px;
    }
  }
`
const OptionalText = styled(Text)`
  display: inline;
`
const Organization = ({
  inDialog,
  properties,
  validate,
  isValidating,
  formErrors,
  renderPanel
}) => {
  const { defaultRegion, isNavianceUser } = useContext(Globals)
  const isManageesRequired = false
  /*
   * Used formerly for isManageesRequired
   *
   *useMemo(
   *  () => ['educator', 'counselor'].includes(properties.navigatorType),
   *  [properties.navigatorType]
   *)
   */
  const {
    register,
    error,
    isInvalid,
    handleOnSubmit,
    setValidation,
    setValue,
    value: formValues
  } = useForm({
    validators: {
      state(form) {
        if (!form.state?.trim()) {
          return t`State is required`
        }
      },
      managees(form) {
        if (formValues.managees?.trim() && isNaN(form.managees)) {
          return t`Number of explorers must be a number`
        }

        if (isManageesRequired && isNaN(form.managees)) {
          return `Please indicate the number of explorers you are working with`
        }
      },
      graduationYear(form) {
        const entered = formValues.graduationYear
        const parsed = form.graduationYear

        if (entered) {
          const maxYear = new Date().getFullYear() + 24

          if (isNaN(parsed) || parsed.toString().length !== 4) {
            return t`Graduation Year must be a four digit number`
          }

          if (parsed < 1920 || parsed > maxYear) {
            return t`Graduation Year must be between 1920 and ${maxYear}`
          }
        }
      }
    },
    initialValues: {
      state: getStateFromAbbreviation(properties.state ?? defaultRegion),
      organizationName: properties.organizationName ?? undefined,
      managees: properties.managees ?? undefined,
      graduationYear: properties.graduationYear ?? undefined
    }
  })
  const shouldAskOrgName = useMemo(() => {
    return (
      properties.isStudent ||
      (properties.navigatorType !== 'family' && !properties.isExplorer)
    )
  }, [properties.isStudent, properties.navigatorType, properties.isExplorer])
  const hasSchool = useMemo(
    () => properties.isStudent || properties.navigatorType === 'educator',
    [properties.isStudent, properties.navigatorType]
  )
  const shouldAskGrade = useMemo(() => {
    return properties.isStudent
  }, [properties.isStudent])
  const onSubmit = useCallback(
    form => {
      validate({
        ...form,
        organizationId: form.organizationName?.id,
        organizationName: form.organizationName?.name ?? form.organizationName,
        organizationType: form.organizationName?.type
      })
    },
    [validate]
  )
  const onClickBack = useCallback(() => {
    if (isNavianceUser) {
      return renderPanel('explorerType')
    }

    if (properties.isBelow13) {
      return renderPanel('basic')
    }

    if (properties.isStudent) {
      return renderPanel('studentInfo')
    }

    if (properties.isExplorer) {
      return renderPanel('explorerType')
    }

    if (properties.navigatorType === 'family') {
      return renderPanel('navigatorType')
    }

    if (properties.navigatorType === 'counselor') {
      return renderPanel('counselorType')
    }

    if (properties.educatorType) {
      if (properties.educatorType === 'k12') {
        return renderPanel('k12Population')
      }

      return renderPanel('educatorType')
    }

    return renderPanel('basic')
  }, [properties, renderPanel, isNavianceUser])
  const searchSchools = useCallback(
    query =>
      loadSchools(
        query,
        error.state
          ? null
          : formValues.state?.value ?? parseStateValueFromCustomInput(formValues.state)
      ),
    [formValues.state, error.state]
  )
  const navigatorType = properties.educatorType
    ? 'educator'
    : properties.counselorType
    ? 'counselor'
    : 'family'

  useEffect(() => {
    if (formErrors) {
      setValidation(formErrors)
    }
  }, [formErrors, setValidation])

  const { seniorYear } = useContext(Globals)
  const graduationYearItems = useMemo(
    () => getFutureGraduationItems(seniorYear),
    [seniorYear]
  )

  useEffect(() => {
    if (properties.graduationYear) {
      setValue({
        graduationYear:
          graduationYearItems.find(item => item.value === properties.graduationYear) ??
          properties.graduationYear
      })
    }
  }, [setValue, graduationYearItems, properties.studentType, properties.graduationYear])

  return (
    <Card inDialog={inDialog}>
      {properties.isExplorer ? (
        <Card.Highlight color={isNavianceUser ? NAVIANCEBLUE : PG}>
          <Explorer type={properties.isStudent ? 'student' : 'jobseeker'} />
        </Card.Highlight>
      ) : (
        <Card.Highlight color={SLB}>
          <Navigator type={navigatorType} />
        </Card.Highlight>
      )}
      <Card.Content>
        <Form id="reg-organization" onSubmit={handleOnSubmit(onSubmit)}>
          <Heading level="2" align="center" mb="12px">
            {!isNavianceUser && properties.isStudent
              ? t`Tell us about your school`
              : t`Tell us about yourself`}
          </Heading>
          <Text align="center" margin="0">
            {!isNavianceUser && properties.isStudent
              ? t`Let us know where you are currently studying.`
              : t`Where are you located?`}
          </Text>
          <Field
            id="states-field"
            htmlFor="states-select"
            isRequired
            width="100%"
            label={t`State`}
            desc={error.state}
            status={error.state ? 'error' : 'default'}>
            <AutoSuggest
              id="states-select"
              labelledBy="states-field"
              size="small"
              items={US_STATES_LIST}
              placeholder={t`Enter your state...`}
              {...register('state', {
                required: true,
                parser: val => val?.value ?? parseStateValueFromCustomInput(val)
              })}
            />
          </Field>
          {shouldAskOrgName && (
            <Field
              id="orgname-field"
              htmlFor="orgname-select"
              width="100%"
              label={
                hasSchool ? (
                  <>
                    {t`School`} <OptionalText>{t`(optional)`}</OptionalText>
                  </>
                ) : (
                  t`Organization`
                )
              }>
              {hasSchool ? (
                <AutoSuggest
                  preload
                  id="orgname-select"
                  labelledBy="orgname-field"
                  size="small"
                  placeholder={t`Start typing for suggestions...`}
                  loadItems={searchSchools}
                  renderItem={renderSchool}
                  {...register('organizationName')}
                />
              ) : (
                <TextInput size="small" {...register('organizationName')} />
              )}
            </Field>
          )}
          {shouldAskGrade && (
            <Field
              id="graduation-field"
              htmlFor="graduation-year"
              width="100%"
              desc={error.graduationYear}
              status={error.graduationYear ? 'error' : 'default'}
              label={(
                <>
                  {t`Graduation Year`} <OptionalText>{t`(optional)`}</OptionalText>
                </>
              )}>
              <AutoSuggest
                id="graduation-year"
                labelledBy="graduation-field"
                size="small"
                placeholder={t`Start typing for suggestions...`}
                items={graduationYearItems}
                {...register('graduationYear', {
                  parser: val => parseInt(val?.value ?? val, 10)
                })}
              />
            </Field>
          )}
          <Controls>
            <Back onClick={onClickBack} />
            <Submit
              text={properties.isBelow13 ? t`Next` : t`Join this site`}
              isLoading={isValidating}
              isDisabled={isInvalid || isValidating}
            />
          </Controls>
        </Form>
      </Card.Content>
      <Card.Detour>
        <Back onClick={onClickBack} />
        <Submit
          text={properties.isBelow13 ? t`Next` : t`Join this site`}
          form="reg-organization"
          isLoading={isValidating}
          isDisabled={isInvalid || isValidating}
        />
      </Card.Detour>
    </Card>
  )
}

Organization.propTypes = { ...props }

export { Organization }
